import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { MfaSetupModal } from './MfaSetupModal'
import { MfaStepUpModal } from './MfaStepUpModal'
import { MfaRecoveryCodesModal } from './MfaRecoveryCodesModal'

interface MfaStatus {
  enabled: boolean
  remaining_recovery_codes: number
}

/** Self-service Two-Factor Authentication section for Account Settings —
 * only rendered for roles that can enroll at all (see MFA_ELIGIBLE_ROLES on
 * the backend: super_admin, atuune_admin, payer_admin). Composes the three
 * MFA modals; this component just owns which one (if any) is open. */
export function MfaSettings({ onMfaStatusChange }: { onMfaStatusChange?: () => void } = {}) {
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [showSetup, setShowSetup] = useState(false)
  const [showDisable, setShowDisable] = useState(false)
  const [showRegenerate, setShowRegenerate] = useState(false)
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  function loadStatus() {
    return api('/mfa/status').then(setStatus)
  }

  useEffect(() => { loadStatus().finally(() => setLoading(false)) }, [])

  if (loading) {
    return (
      <div className="card" style={{ maxWidth: 560, marginBottom: 16 }}>
        <h3>Two-Factor Authentication</h3>
        <p className="muted" style={{ fontSize: 13.5 }}>Loading…</p>
      </div>
    )
  }

  return (
    <div className="card" style={{ maxWidth: 560, marginBottom: 16 }}>
      <h3>Two-Factor Authentication</h3>

      {status?.enabled ? (
        <>
          <p className="muted" style={{ fontSize: 13.5 }}>
            Enabled. {status.remaining_recovery_codes} unused recovery code{status.remaining_recovery_codes === 1 ? '' : 's'} remaining.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn outline" onClick={() => setShowRegenerate(true)}>Regenerate recovery codes</button>
            <button className="btn outline" onClick={() => setShowDisable(true)}>Disable two-factor authentication</button>
          </div>
        </>
      ) : (
        <>
          <p className="muted" style={{ fontSize: 13.5 }}>
            Add an extra layer of security using an authenticator app (Google Authenticator, 1Password, Authy, etc).
          </p>
          <button className="btn outline" onClick={() => setShowSetup(true)}>Enable two-factor authentication</button>
        </>
      )}

      {statusMessage && <p role="status" style={{ fontSize: 13.5, marginTop: 10 }}>{statusMessage}</p>}

      {showSetup && (
        <MfaSetupModal
          onClose={() => setShowSetup(false)}
          onEnabled={() => { setShowSetup(false); setStatusMessage('Two-factor authentication is now enabled.'); loadStatus(); onMfaStatusChange?.() }}
        />
      )}

      {showDisable && (
        <MfaStepUpModal
          title="Disable two-factor authentication"
          description="Enter your current code, or a recovery code, to confirm."
          onClose={() => setShowDisable(false)}
          onVerified={async (stepUpToken) => {
            await api('/mfa/disable', { method: 'POST', headers: { 'X-Step-Up-Token': stepUpToken } })
            setShowDisable(false)
            setStatusMessage('Two-factor authentication has been disabled.')
            loadStatus()
            onMfaStatusChange?.()
          }}
        />
      )}

      {showRegenerate && (
        <MfaStepUpModal
          title="Regenerate recovery codes"
          description="This immediately invalidates your existing recovery codes. Enter your current code, or a recovery code, to confirm."
          onClose={() => setShowRegenerate(false)}
          onVerified={async (stepUpToken) => {
            const { recovery_codes } = await api('/mfa/recovery-codes/regenerate', { method: 'POST', headers: { 'X-Step-Up-Token': stepUpToken } })
            setShowRegenerate(false)
            setNewRecoveryCodes(recovery_codes)
            loadStatus()
          }}
        />
      )}

      {newRecoveryCodes && (
        <MfaRecoveryCodesModal
          codes={newRecoveryCodes}
          onClose={() => { setNewRecoveryCodes(null); setStatusMessage('New recovery codes generated.') }}
        />
      )}
    </div>
  )
}
